/**
 * Player State Store
 * Uses Zustand for global player state management
 */

import { create } from 'zustand';
import type { RoomState, PlaylistItem } from '../hooks/useRoomWebSocket';

interface PlayerState {
  // Room state from WebSocket
  roomState: RoomState | null;
  isConnected: boolean;
  isPaired: boolean;

  // Playlist data
  playlist: PlaylistItem[];
  currentIndex: number;

  // Playback state
  isPlaying: boolean;
  currentTimeMs: number;

  // Preloaded content
  preloadedUrls: Map<string, string>;

  // UI state
  showQRCode: boolean;
  showControls: boolean;
  error: string | null;

  // Actions
  setRoomState: (state: RoomState) => void;
  setConnected: (connected: boolean) => void;
  setPaired: (paired: boolean) => void;
  setPlaylist: (playlist: PlaylistItem[]) => void;
  setCurrentIndex: (index: number) => void;
  nextItem: () => void;
  prevItem: () => void;
  setPlaying: (playing: boolean) => void;
  setCurrentTimeMs: (timeMs: number) => void;
  setPreloadedUrl: (artworkId: string, url: string) => void;
  clearPreloadedUrls: () => void;
  setShowQRCode: (show: boolean) => void;
  setShowControls: (show: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  roomState: null,
  isConnected: false,
  isPaired: false,
  playlist: [],
  currentIndex: 0,
  isPlaying: false,
  currentTimeMs: 0,
  preloadedUrls: new Map<string, string>(),
  showQRCode: true,
  showControls: false,
  error: null,
};

export const usePlayerStore = create<PlayerState>((set, get) => ({
  ...initialState,

  setRoomState: (state) => {
    set({ roomState: state });
    // Update playing state from room
    if (state) {
      set({ isPlaying: !state.isPaused });
    }
  },

  setConnected: (connected) => set({ isConnected: connected }),

  setPaired: (paired) => {
    set({ isPaired: paired });
    if (paired) {
      set({ showQRCode: false });
    }
  },

  setPlaylist: (playlist) => set({ playlist }),

  setCurrentIndex: (index) => {
    const { playlist } = get();
    if (index >= 0 && index < playlist.length) {
      set({ currentIndex: index, currentTimeMs: 0 });
    }
  },

  nextItem: () => {
    const { playlist, currentIndex } = get();
    if (playlist.length === 0) return;
    const nextIndex = (currentIndex + 1) % playlist.length;
    set({ currentIndex: nextIndex, currentTimeMs: 0 });
  },

  prevItem: () => {
    const { playlist, currentIndex } = get();
    if (playlist.length === 0) return;
    const prevIndex = currentIndex === 0 ? playlist.length - 1 : currentIndex - 1;
    set({ currentIndex: prevIndex, currentTimeMs: 0 });
  },

  setPlaying: (playing) => set({ isPlaying: playing }),

  setCurrentTimeMs: (timeMs) => set({ currentTimeMs: timeMs }),

  setPreloadedUrl: (artworkId, url) => {
    const { preloadedUrls } = get();
    const newMap = new Map(preloadedUrls);
    newMap.set(artworkId, url);
    set({ preloadedUrls: newMap });
  },

  clearPreloadedUrls: () => set({ preloadedUrls: new Map() }),

  setShowQRCode: (show) => set({ showQRCode: show }),

  setShowControls: (show) => set({ showControls: show }),

  setError: (error) => set({ error }),

  reset: () => set(initialState),
}));

/**
 * Get the current playlist item
 */
export function useCurrentItem(): PlaylistItem | null {
  const { playlist, currentIndex } = usePlayerStore();
  return playlist[currentIndex] || null;
}

/**
 * Get items to preload (next N items)
 */
export function useItemsToPreload(count: number = 2): PlaylistItem[] {
  const { playlist, currentIndex } = usePlayerStore();
  if (playlist.length === 0) return [];

  const items: PlaylistItem[] = [];
  for (let i = 1; i <= count; i++) {
    const index = (currentIndex + i) % playlist.length;
    if (playlist[index]) {
      items.push(playlist[index]);
    }
  }
  return items;
}
